import { urlHref } from "@salestrak/custom-fields";
import { stripHtmlToText } from "@salestrak/utilities";
import { toastManager, type EditableValueCellAction, type IconName } from "@salestrak/ui";
import { countryCodeMap, NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";

/**
 * The field-type descriptor registry: one entry per field type declaring how
 * its VALUE behaves in an inline cell — which editor composition the cell
 * builds, which value actions it offers, the noun its action labels use and
 * the protocol verb/href for open-style actions.
 *
 * This is behavioral metadata. Visual metadata (icon, label, color) stays in
 * `lib/constants/field-type-icons.ts` (`FIELD_TYPE_META`), which many
 * pickers/tables already read.
 *
 * Issue PROPERTIES ride the same registry where they share a type — the
 * built-in Address column reads the `address` entry — so a property cell and
 * a custom-field cell of the same type cannot drift apart.
 */

/** `open` follows the value's protocol (mailto / tel / link); `map` jumps to
 *  the workspace map focused on the address. */
export type FieldValueAction = "open" | "map" | "copy" | "clear" | "fullscreen";

/**
 * Which editor composition `EditableValueCell` gets:
 * - `inline`    — generic in-cell editor (`CustomFieldRenderer`) behind the
 *                 host-driven active state
 * - `date`      — `FieldDatePicker` overlay on the cell trigger (the field's
 *                 type + `includeTime` config pick the picker mode)
 * - `select`    — `Select` overlay on the cell trigger
 * - `member`    — `UserSelector` overlay, mounted only while open
 * - `paragraph` — rich-text popover (desktop) / drawer (mobile)
 * - `checkbox`  — toggles in place, no edit mode
 * - `photos`    — tile grid edits in place, row grows with content
 */
export type FieldEditorKind =
  | "inline"
  | "date"
  | "select"
  | "member"
  | "paragraph"
  | "checkbox"
  | "photos";

export type FieldTypeDescriptor = {
  editor: FieldEditorKind;
  /** Value actions offered while the cell holds a value, in render order.
   *  An empty value always shows no actions. */
  actions: readonly FieldValueAction[];
  /** Human noun for action labels ("Copy email", "Clear phone number"). */
  actionNoun: string;
  /** Verb for the protocol "open" action ("Call", "Email", "Open"). */
  protocolVerb?: string;
  /** Hover tooltip for the protocol "open" action when the bare verb does
   *  not say where the action leads ("Open link in new tab"). Defaults to
   *  the verb. */
  protocolTooltip?: string;
  /** Icon for the protocol "open" action button. Types whose protocol is a
   *  device action wear their own glyph (Call wears the phone); the default
   *  "external-link" reads as leaving for another surface. */
  protocolIcon?: IconName;
};

// Cell click edits; the protocol (mailto/tel/open link) is a button instead.
const PROTOCOL_ACTIONS = ["open", "copy", "clear"] as const;
const COPY_AND_CLEAR = ["copy", "clear"] as const;
// An address leads with its place on the map; the cell click still edits.
const ADDRESS_ACTIONS = ["map", "copy", "clear"] as const;
// A checkbox has no value actions: copying a boolean is meaningless and
// "clear" writes `false`, which unchecking already does.
const NO_ACTIONS = [] as const;
// Copying photo ids is meaningless — view them full screen instead.
const PHOTOS_ACTIONS = ["fullscreen", "clear"] as const;

export const FIELD_TYPE_DESCRIPTORS: Record<string, FieldTypeDescriptor> = {
  text: { editor: "inline", actions: COPY_AND_CLEAR, actionNoun: "text" },
  number: { editor: "inline", actions: COPY_AND_CLEAR, actionNoun: "number" },
  money: { editor: "inline", actions: COPY_AND_CLEAR, actionNoun: "value" },
  email: {
    editor: "inline",
    actions: PROTOCOL_ACTIONS,
    actionNoun: "email",
    protocolVerb: "Email",
    protocolIcon: "mail",
  },
  phone: {
    editor: "inline",
    actions: PROTOCOL_ACTIONS,
    actionNoun: "phone number",
    protocolVerb: "Call",
    protocolIcon: "phone",
  },
  url: {
    editor: "inline",
    actions: PROTOCOL_ACTIONS,
    actionNoun: "website",
    protocolVerb: "Open",
    protocolTooltip: "Open link in new tab",
  },
  address: { editor: "inline", actions: ADDRESS_ACTIONS, actionNoun: "address" },
  paragraph: {
    editor: "paragraph",
    actions: COPY_AND_CLEAR,
    actionNoun: "text area",
  },
  // `labels` is the multi twin of `select` — the same picker, by type.
  select: { editor: "select", actions: COPY_AND_CLEAR, actionNoun: "value" },
  labels: { editor: "select", actions: COPY_AND_CLEAR, actionNoun: "value" },
  // A calendar day and an instant are one type: `date`, with `includeTime`
  // choosing. `time` is its own type (a clock, no calendar day).
  date: { editor: "date", actions: COPY_AND_CLEAR, actionNoun: "value" },
  time: { editor: "date", actions: COPY_AND_CLEAR, actionNoun: "value" },
  member: { editor: "member", actions: COPY_AND_CLEAR, actionNoun: "value" },
  checkbox: { editor: "checkbox", actions: NO_ACTIONS, actionNoun: "value" },
  photos: { editor: "photos", actions: PHOTOS_ACTIONS, actionNoun: "value" },
};

/** Unknown types (server-added, not yet supported here) edit through the
 *  generic inline branch, whose renderer shows "Unsupported field type". */
const FALLBACK_DESCRIPTOR: FieldTypeDescriptor = {
  editor: "inline",
  actions: COPY_AND_CLEAR,
  actionNoun: "value",
};

export function getFieldTypeDescriptor(type: string): FieldTypeDescriptor {
  return FIELD_TYPE_DESCRIPTORS[type] ?? FALLBACK_DESCRIPTOR;
}

/** The text an action operates on (what "copy" copies, what a protocol href
 *  is built from) — null when the value can't produce one.
 *
 *  Keyed by the field TYPE, not a whole definition: a built-in property has no
 *  `CustomFieldDef` to pass, and taking one would force every property cell to
 *  fabricate a fake def to reach the shared behavior. */
export function getValueActionText(
  type: string,
  value: unknown,
  displayValue: string,
): string | null {
  switch (type) {
    case "phone":
      return getPhoneActionValue(value, displayValue);
    case "url": {
      const text = String(value ?? displayValue).trim();
      if (!text || text === NONE_OPTION_LABEL) return null;
      return normalizeWebsiteUrl(text) ?? text;
    }
    case "paragraph": {
      // Stored as HTML — copy the plain text, not the markup.
      const text = stripHtmlToText(String(value ?? "")).trim();
      return text ? text : null;
    }
    default: {
      const text = displayValue.trim();
      return text && text !== NONE_OPTION_LABEL ? text : null;
    }
  }
}

export function getProtocolHref(
  type: string,
  actionText: string | null,
): string | null {
  if (!actionText || !getFieldTypeDescriptor(type).protocolVerb) return null;
  switch (type) {
    case "email":
      return `mailto:${actionText}`;
    case "phone":
      return `tel:${actionText}`;
    case "url":
      return normalizeWebsiteUrl(actionText);
    default:
      return null;
  }
}

/**
 * The standard "copy" value action: resolve the text at click time, copy it,
 * toast the outcome, drop focus. Shared by the custom-field cell and the
 * built-in property cells so copy UX cannot fork per column.
 */
export function buildCopyValueAction(
  noun: string,
  resolveText: () => string | null,
): EditableValueCellAction {
  return {
    key: "copy",
    icon: "copy",
    label: `Copy ${noun}`,
    tooltip: "Copy",
    onSelect: () => {
      void (async () => {
        const text = resolveText();
        if (!text) return;
        const ok = await copyToClipboard(text);
        if (ok) {
          toastManager.add({
            title: `${sentenceCase(noun)} copied`,
            type: "success",
          });
        } else {
          toastManager.add({
            title: `Failed to copy ${noun}`,
            type: "error",
          });
        }
        blurActiveElement();
      })();
    },
  };
}

export function sentenceCase(value: string): string {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

export function blurActiveElement(): void {
  if (typeof document === "undefined") return;
  (document.activeElement as HTMLElement | null)?.blur?.();
}

function normalizeWebsiteUrl(value: string): string | null {
  return value.trim() === NONE_OPTION_LABEL ? null : urlHref(value);
}

function normalizeDialValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const sanitized = trimmed.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return sanitized || null;
}

function getPhoneActionValue(
  value: unknown,
  displayValue: string,
): string | null {
  if (typeof value === "object" && value !== null) {
    const phone = (value as { value?: unknown }).value;
    const phoneText = phone == null ? "" : String(phone).trim();
    if (!phoneText) return null;
    if (phoneText.startsWith("+")) return normalizeDialValue(phoneText);
    const country = (value as { country?: unknown }).country;
    const countryCode = (value as { countryCode?: unknown }).countryCode;
    const countryKey = String(country ?? countryCode ?? "");
    const dialCode = countryKey.startsWith("+")
      ? countryKey
      : countryCodeMap[countryKey]?.code;
    return normalizeDialValue(`${dialCode ?? ""}${phoneText}`);
  }
  return normalizeDialValue(String(value ?? displayValue));
}
